import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  ApiError,
  boardCardKeyPatchSchema,
  boardCardNumbersSettingPatchSchema,
  boardCardNumbersSettingSchema,
  boardSchema,
  type Board,
  type BoardCardKeyPatch,
  type BoardCardNumbersSetting,
  type BoardCardNumbersSettingPatch,
  type BoardContents,
} from '@molt/shared';
import { patch } from './api';
import { setBoardContentsQueries } from './cross-board-move';
import { queryKeys } from './query-keys';

/**
 * The board key and the show-card-numbers setting from the board settings
 * panel — `docs/notes/card-keys.md`, "Web".
 *
 * Two routes, two field-scoped patches (SC-4):
 *
 *  - `PATCH /boards/:id/card-numbers-on-front` — `FR-CARD-005`'s optional
 *    setting. It emits no event (the vocabulary has no name for it), so no
 *    socket frame will refresh this tab: the response is written into every
 *    cached contents entry for the board, `applyBoardCardAgingModeSuccess`'s
 *    shape.
 *  - `PATCH /boards/:id/key` — the board key. Every card's `ref` and every card
 *    `path` on the board changes with it, so this invalidates everything keyed
 *    under the board and every card query rather than patching the cache by
 *    hand; `board.key_changed` reaches OTHER tabs over the socket, where
 *    `eventBlastRadius` invalidates the same board prefix.
 */

export function updateBoardCardNumbersSetting(
  boardId: string,
  body: BoardCardNumbersSettingPatch,
): Promise<BoardCardNumbersSetting> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/card-numbers-on-front`,
    boardCardNumbersSettingSchema,
    boardCardNumbersSettingPatchSchema.parse(body),
  );
}

export function applyBoardCardNumbersSettingToBoardContents(
  previous: BoardContents | undefined,
  state: BoardCardNumbersSetting,
): BoardContents | undefined {
  if (previous === undefined) return undefined;
  if (previous.board.id !== state.boardId) return previous;
  if (previous.board.showCardNumbersOnFront === state.showCardNumbersOnFront) {
    return previous;
  }
  return {
    ...previous,
    board: { ...previous.board, showCardNumbersOnFront: state.showCardNumbersOnFront },
  };
}

export function applyBoardCardNumbersSettingSuccess(
  queryClient: QueryClient,
  state: BoardCardNumbersSetting,
): void {
  setBoardContentsQueries(queryClient, state.boardId, (previous) =>
    applyBoardCardNumbersSettingToBoardContents(previous, state),
  );
  void queryClient.invalidateQueries({
    queryKey: queryKeys.boards.contents(state.boardId),
  });
}

export function useUpdateBoardCardNumbersSetting(
  boardId: string,
): UseMutationResult<BoardCardNumbersSetting, ApiError, BoardCardNumbersSettingPatch> {
  const queryClient = useQueryClient();

  return useMutation<BoardCardNumbersSetting, ApiError, BoardCardNumbersSettingPatch>({
    mutationFn: (body) => updateBoardCardNumbersSetting(boardId, body),
    onSuccess: (state) => {
      applyBoardCardNumbersSettingSuccess(queryClient, state);
    },
  });
}

export function updateBoardCardKey(
  boardId: string,
  body: BoardCardKeyPatch,
): Promise<Board> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/key`,
    boardSchema,
    boardCardKeyPatchSchema.parse(body),
  );
}

/**
 * `true` for the route's "another board holds this key" refusal — 409 with
 * `details.key = ['key_taken']`. A closed board is ALSO a 409, with no
 * details, and must not read as a taken key.
 */
export function isCardKeyTaken(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.code === 'conflict' &&
    error.details?.['key']?.includes('key_taken') === true
  );
}

export function applyBoardCardKeySuccess(queryClient: QueryClient, board: Board): void {
  setBoardContentsQueries(queryClient, board.id, (previous) =>
    previous === undefined || previous.board.cardKey === board.cardKey
      ? previous
      : { ...previous, board: { ...previous.board, cardKey: board.cardKey } },
  );
  // Every `ref` and every card `path` on the board moved: refetch rather than
  // rebuild them here, which would be a second copy of the server's rule.
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.detail(board.id) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.cards.all() });
}

export function useUpdateBoardCardKey(
  boardId: string,
): UseMutationResult<Board, ApiError, BoardCardKeyPatch> {
  const queryClient = useQueryClient();

  return useMutation<Board, ApiError, BoardCardKeyPatch>({
    mutationFn: (body) => updateBoardCardKey(boardId, body),
    onSuccess: (board) => {
      applyBoardCardKeySuccess(queryClient, board);
    },
  });
}
